import React, { ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Link, NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import apiManager from '@/api/AxiosInstance';
import BasicButton from '@/components/Common/Button/BasicButton';
import PasswordSuffix from '@/components/Common/Input/TextInput/PasswordSuffix';
import TextInputLabelOff from '@/components/Common/Input/TextInputLabelOff';
import BasicTabs from '@/components/Common/Tabs/BasicTabs';
import BubbleComponent from '@/components/Common/Tooltip/BubbleComponent';
import MenuHeader from '@/components/Header/MenuHeader';
import { colors } from '@/const/colors';
import { IMAGE_BASE_URL, KAKAO_REDIRECT_URL, NAVER_AUTH_URL } from '@/const/url';
import { useToastContext } from '@/contexts/Common/ToastContext';
import useNavigateBack from '@/hooks/useNavigateBack';
import useSetMemberData from '@/hooks/useSetMemberData';

interface LoginInfo {
  phoneNumber: string;
  password: string;
}

const LOGIN_TAB_GRADIENT = 'linear-gradient(276deg, #535AFF 0.12%, #397AFF 50%, #10ACFF 99.88%)';

const Login = () => {
  const buttonRef = useRef<HTMLDivElement>(null);
  // const [bubblePosition, setBubblePosition] = useState(0);

  const [isShowPassword, setIsShowPassword] = useState(false);

  useEffect(() => {
    if (buttonRef.current) {
      const buttonWidth = buttonRef.current.clientWidth;
      // setBubblePosition(buttonWidth / 4);
    }
  }, []);

  const navigate = useNavigate();
  const navigateBack = useNavigateBack();
  const setMemberData = useSetMemberData('');
  const navigationType = useNavigationType();
  const location = useLocation();
  const redirectUrl = location.state?.redirectUrl;
  const [isSocialLogin, setIsSocialLogin] = useState<boolean>(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const { showToast } = useToastContext();
  const [cookies, setCookie, removeCookie] = useCookies(['redirect_url']);

  const [tabNum, setTabNum] = useState(0);

  const loginTabLabelList: TabType[] = [
    { icon: null, title: '일반 회원' },
    { icon: null, title: '딜러 회원' },
  ];
  const isDealerTab = tabNum === 1;

  const phoneNumberRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const [loginInfo, setLoginInfo] = useState<LoginInfo>({
    phoneNumber: '',
    password: '',
  });
  const [isErrorInput, setIsErrorInput] = useState(false);

  const onChangeMemberInfo = (e: ChangeEvent<HTMLInputElement>) => {
    setLoginInfo({ ...loginInfo, [e.target.name]: e.target.value });
  };

  const onChangeLoginType = () => {
    setIsSocialLogin(!isSocialLogin);
    setRedirectUrlCookie();
  };

  const goToPage = (href: string, state?: { signUpType?: string }) => {
    navigate(href, state ? { state } : undefined);
  };

  const goHref = (href: string) => {
    window.location.href = `${href}`;
  };

  const initKakao = () => {
    const Kakao = window.Kakao;
    if (Kakao && !Kakao.isInitialized()) {
      Kakao.init(process.env.KAKAO_KEY);
    }
  };

  useEffect(() => {
    initKakao();
  }, []);

  const setRedirectUrlCookie = () => {
    // 쿠키와 localStorage 모두에 저장하여 안정성 향상
    const redirectUrlValue = redirectUrl || '/';
    const expireDate = new Date();
    expireDate.setMinutes(expireDate.getMinutes() + 10);
    setCookie('redirect_url', redirectUrlValue, { expires: expireDate, path: '/' });
    // localStorage에도 저장 (소셜 로그인 시 쿠키가 설정되지 않을 수 있어서)
    localStorage.setItem('redirect_url', redirectUrlValue);
  };

  const loginWithKakao = () => {
    setRedirectUrlCookie();

    const Kakao = window.Kakao;
    Kakao.Auth.authorize({
      redirectUri: KAKAO_REDIRECT_URL,
      scope: 'name,profile_image,phone_number,account_email',
      prompt: 'select_account',
      throughTalk: true,
    });
  };

  const loginWithNaver = () => {
    setRedirectUrlCookie();
    goHref(`${NAVER_AUTH_URL}`);
  };

  const onClickLogin = () => {
    const loginPayload = {
      ...loginInfo,
      type: isDealerTab ? 'DEALER' : 'NORMAL',
    };
    apiManager
      .post('/auth/login', loginPayload, {
        withCredentials: false,
      })
      .then((response) => {
        setIsErrorInput(false);
        const { token, loginByTempPassword } = response.data;
        localStorage.setItem('token', token);

        const deviceToken = localStorage.getItem('deviceToken');
        if (deviceToken) {
          apiManager.post('/api/v1/fire-base/device-token', { deviceToken: deviceToken });
        }

        if (loginByTempPassword) {
          navigate('/member/password');
          setMemberData(token);
        } else {
          navigate(redirectUrl || '/');
          setMemberData(token);
        }
      })
      .catch((error) => {
        const loginErrorCodes = ['PASSWORD_IS_EMPTY', 'PHONE_NUMBER_IS_EMPTY', 'PHONE_NUMBER_OR_PASSWORD_IS_WRONG'];
        if (loginErrorCodes.includes(error?.code)) {
          showToast(`${error.message}`, 'error', 'bottom');
        } else {
          showToast(error.message, 'error', 'bottom');
        }
        setIsErrorInput(true);
      });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (!!!loginInfo.phoneNumber) {
        showToast(`휴대폰 번호를 입력해주세요.`, 'error', 'top');
        return;
      } else if (!!!loginInfo.password) {
        showToast(`비밀번호를 입력해주세요.`, 'error', 'top');
        return;
      } else {
        onClickLogin();
      }
    }
  };

  useEffect(() => {
    if (localStorage.getItem('token') && navigationType === NavigationType.Pop) {
      navigateBack();
    }
    if (location.state?.isSocial === false) {
      setIsSocialLogin(false);
    } else {
      setIsSocialLogin(true);
    }
  }, []);

  // SignUp 등에서 소셜 로그인 실패로 넘어올 때 토스트 표시
  useEffect(() => {
    const message = location.state?.socialErrorMessage;
    if (message) {
      showToast(message, 'error', 'bottom');
    }
  }, [location.state?.socialErrorMessage, showToast]);

  const scrollIntoView = (e?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e) {
      const { name } = e.target;
      if (name === 'phoneNumber') {
        phoneNumberRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'password') {
        passwordRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    } else {
      phoneNumberRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      passwordRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  };

  useLayoutEffect(() => {
    const initialHeight = document.documentElement.clientHeight;

    if (initialHeight < 560) {
      setIsKeyboardOpen(true);
    }

    const handleResize = () => {
      const currentHeight = window.innerHeight;
      setIsKeyboardOpen(initialHeight > currentHeight);
    };

    const detectMobileKeyboard = () => {
      handleResize();
      scrollIntoView();
    };

    window.addEventListener('resize', detectMobileKeyboard);
    return () => {
      window.removeEventListener('resize', detectMobileKeyboard);
    };
  });

  const togglePasswordVisibility = (isShow: boolean, setIsShow: React.Dispatch<React.SetStateAction<boolean>>) => {
    setIsShow(!isShow);
  };

  const onChangeLoginTab = (newValue: number) => {
    setTabNum(newValue);
    return true;
  };

  const showSocialLoginArea = !isDealerTab && isSocialLogin;

  return (
    <>
      <div
        className={`w-full h-[100dvh] ${
          isDealerTab ? 'bg-dealer-gradient' : ''
        } flex flex-col justify-between relative min-h-[540px] pt-[60px]`}
      >
        <MenuHeader title="로그인"></MenuHeader>
        <div className="w-full max-w-[720px] mx-auto ">
          <BasicTabs
            labelList={loginTabLabelList}
            value={tabNum}
            onChange={onChangeLoginTab}
            indicatorColor={isDealerTab ? 'transparent' : LOGIN_TAB_GRADIENT}
            tabSx={{ color: colors.gray[6] }}
            activeTabSx={
              isDealerTab
                ? { color: '#2D3E50', fontWeight: 700 }
                : {
                    background: LOGIN_TAB_GRADIENT,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontWeight: 700,
                  }
            }
          />
        </div>

        <div className={`${isDealerTab ? '' : 'px-4 pb-4'}  `}>
          <div
            className={`absolute ${
              isKeyboardOpen ? 'top-[140px]' : 'top-[20%]'
            } left-4 font-bold text-4xl xxs:text-3xl xs:text-3xl leading-normal ml-2 mt-15 ${
              isDealerTab ? 'text-white' : 'text-black'
            }`}
          >
            {!isDealerTab ? (
              <img src={`${IMAGE_BASE_URL}/logo_gra.png`} alt="직트럭 로고" className="w-20 mb-4"></img>
            ) : (
              <img
                src={`https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/logo_white.png`}
                alt="직트럭 로고"
                className="w-20 mb-4"
              ></img>
            )}
            화물차 거래의
            <br /> 새로운 기준
            {isDealerTab && (
              <p className="text-white text-sm font-semibold px-4 py-[5px] bg-dealer-1 rounded-full w-fit mt-3">
                DEALER
              </p>
            )}
          </div>

          {!showSocialLoginArea ? (
            <div className="bg-white rounded-t-2xl p-4 ">
              <div className="pt-8">
                <div className="flex flex-col gap-[30px]">
                  <TextInputLabelOff
                    ref={phoneNumberRef}
                    name="phoneNumber"
                    placeholder="휴대폰 번호를 입력하세요."
                    value={loginInfo.phoneNumber}
                    fontSize={18}
                    onChange={(e) => onChangeMemberInfo(e)}
                    onFocus={(e) => scrollIntoView(e)}
                    error={isErrorInput}
                    type="number"
                    inputMode="numeric"
                  ></TextInputLabelOff>
                  <TextInputLabelOff
                    ref={passwordRef}
                    name="password"
                    placeholder="비밀번호를 입력하세요."
                    value={loginInfo.password}
                    fontSize={18}
                    onChange={(e) => onChangeMemberInfo(e)}
                    onFocus={(e) => scrollIntoView(e)}
                    error={isErrorInput}
                    type={isShowPassword ? 'text' : 'password'}
                    onKeyDown={(e) => handleKeyPress(e)}
                    suffix={
                      <PasswordSuffix
                        isShow={isShowPassword}
                        toggleVisibility={() => togglePasswordVisibility(isShowPassword, setIsShowPassword)}
                      />
                    }
                  ></TextInputLabelOff>
                </div>
                <div className={`flex justify-between mt-[18px]  ${isDealerTab ? 'mb-12' : 'mb-[18px]'}`}>
                  <a
                    className="block text-sm font-semibold underline cursor-pointer text-dealer-0 underline-offset-4"
                    onClick={() => goToPage('/signup', isDealerTab ? { signUpType: 'DEALER' } : undefined)}
                  >
                    회원가입
                  </a>
                  <Link to="/forgot-password">
                    <span className="block text-sm font-semibold cursor-pointer text-gray-6">비밀번호 찾기</span>
                  </Link>
                </div>
              </div>

              <BasicButton
                name="로그인"
                bgColor={colors.dealer[0]}
                borderColor={colors.dealer[0]}
                textColor={colors.gray[0]}
                fontSize={16}
                height={48}
                borderRadius={10}
                fontWeight="semibold"
                onClick={() => onClickLogin()}
                isDisabled={!loginInfo.phoneNumber || !loginInfo.password}
              ></BasicButton>
            </div>
          ) : (
            <div>
              <div>
                <div className="flex flex-col gap-[30px]">
                  <TextInputLabelOff
                    ref={phoneNumberRef}
                    name="phoneNumber"
                    placeholder="휴대폰 번호를 입력하세요."
                    value={loginInfo.phoneNumber}
                    fontSize={18}
                    onChange={(e) => onChangeMemberInfo(e)}
                    onFocus={(e) => scrollIntoView(e)}
                    error={isErrorInput}
                    type="number"
                    inputMode="numeric"
                  ></TextInputLabelOff>
                  <TextInputLabelOff
                    ref={passwordRef}
                    name="password"
                    placeholder="비밀번호를 입력하세요."
                    value={loginInfo.password}
                    fontSize={18}
                    onChange={(e) => onChangeMemberInfo(e)}
                    onFocus={(e) => scrollIntoView(e)}
                    error={isErrorInput}
                    type={isShowPassword ? 'text' : 'password'}
                    onKeyDown={(e) => handleKeyPress(e)}
                    suffix={
                      <PasswordSuffix
                        isShow={isShowPassword}
                        toggleVisibility={() => togglePasswordVisibility(isShowPassword, setIsShowPassword)}
                      />
                    }
                  ></TextInputLabelOff>
                </div>
                <div className={`flex justify-between mt-[18px]  ${isDealerTab ? 'mb-12' : 'mb-[18px]'}`}>
                  <a
                    className="block text-sm font-semibold underline cursor-pointer text-primary-10 underline-offset-4"
                    onClick={() => goToPage('/signup', isDealerTab ? { signUpType: 'DEALER' } : undefined)}
                  >
                    회원가입
                  </a>
                  <Link to="/forgot-password">
                    <span className="block text-sm font-semibold cursor-pointer text-gray-6">비밀번호 찾기</span>
                  </Link>
                </div>
              </div>

              <BasicButton
                name="로그인"
                bgColor={colors.primary[10]}
                borderColor={colors.primary[10]}
                textColor={colors.gray[0]}
                fontSize={16}
                height={48}
                borderRadius={10}
                fontWeight="semibold"
                onClick={() => onClickLogin()}
                isDisabled={!loginInfo.phoneNumber || !loginInfo.password}
              ></BasicButton>

              <div className="flex justify-center items-center my-4">
                <div className="w-full h-[1px] bg-gray-3"></div>
                <span className="text-gray-6 text-[12px] mx-4 whitespace-nowrap">또는</span>
                <div className="w-full h-[1px] bg-gray-3"></div>
              </div>

              <div className="flex gap-4">
                <BasicButton
                  className="pl-3"
                  name="카카오톡"
                  icon={
                    <img
                      src="https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/kakao_icon.png"
                      alt="kakao_icon"
                      className="absolute w-8 h-8 left-4 top-[50%] translate-y-[-50%] sm280:w-6 sm280:h-6"
                    ></img>
                  }
                  bgColor={colors.kakao}
                  borderColor={colors.kakao}
                  textColor={colors.gray[10]}
                  fontSize={16}
                  height={56}
                  fontWeight="bold"
                  onClick={() => {
                    loginWithKakao();
                  }}
                ></BasicButton>
                <BasicButton
                  className="pl-2"
                  name="네이버"
                  icon={
                    <img
                      src="https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/naver_icon.png"
                      alt="naver_icon"
                      className="absolute w-8 h-8 left-4 top-[50%] translate-y-[-50%] sm280:w-6 sm280:h-6"
                    ></img>
                  }
                  bgColor={colors.naver}
                  borderColor={colors.naver}
                  textColor={colors.gray[0]}
                  fontSize={16}
                  height={56}
                  borderRadius={12}
                  fontWeight="bold"
                  onClick={loginWithNaver}
                ></BasicButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Login;
